import { Box } from '@mui/material'
import { SchedulerPageFrame } from '../SchedulerBrandBar'
import { useState } from 'react'
import TableRowsIcon from '@mui/icons-material/TableRows'
import { SchDisplay, SchedulerThemeScope, TOKENS } from '../SchedulerDesign'
import { SOR_TABLE_PANEL_SX, SORSidebar } from './SOR.design'
import { SORView } from './SOR.view'
import { SORSynchronizedLines } from './SORSynchronizedLines.view'
import { SORSterilizerRouting } from './SORSterilizerRouting.view'
import { SORTanks } from './SORTanks.view'
import { SORKitchenCycles } from './SORKitchenCycles.view'
import { SchedulerPackagingRoutesRowType } from '../Scheduler.types'

// Section ids & labels mirror SchedulerInput.page.tsx RAW_DATA_ITEMS so the
// master SOR page and the per-scheduler Raw Data sidebar stay visually identical.
const SECTIONS = {
  PRODUCTION_REQUIREMENT: 'tab-0',
  WORKSTATION_DOWNTIME: 'tab-1',
  CLEANING_MAPPING: 'tab-2',
  RESOURCE_AND_MATERIAL_CONSTRAINTS: 'tab-3',
  RESOURCE_AND_INPUTS: 'tab-4',
  PACKAGING_ROUTES: 'tab-5',
  RESOURCE_AND_OUTPUT_MAPPING: 'tab-6',
  PRODUCT_MASTER: 'tab-7',
  PRODUCT_CLEAN_CHANGEOVER: 'tab-8',
  BASE_MATERIAL_CLEAN_CHANGEOVER: 'tab-9',
  SYNCHRONIZED_LINES: 'tab-10',
  STERILISER_TYPES: 'tab-11',
  STERILISER_ROUTING: 'tab-12',
  RESOURCE_SWITCHING: 'tab-13',
  FORMAT_CHANGEOVER: 'tab-14',
  RESOURCE_CAPACITY_PROFILE: 'tab-15',
  TANKS: 'tab-16',
  KITCHEN_CYCLES: 'tab-17',
}

const RAW_DATA_ITEMS = [
  { id: SECTIONS.PRODUCTION_REQUIREMENT, label: 'Production Requirement' },
  { id: SECTIONS.WORKSTATION_DOWNTIME, label: 'Workstation Downtime' },
  { id: SECTIONS.PRODUCT_MASTER, label: 'Product Master Data' },
  { id: SECTIONS.PACKAGING_ROUTES, label: 'Packaging Routes' },
  { id: SECTIONS.CLEANING_MAPPING, label: 'Cleaning Mapping' },
  {
    id: SECTIONS.RESOURCE_AND_MATERIAL_CONSTRAINTS,
    label: 'Resource & Material Constraints',
  },
  { id: SECTIONS.RESOURCE_AND_INPUTS, label: 'Resource & Input Mapping' },
  {
    id: SECTIONS.RESOURCE_AND_OUTPUT_MAPPING,
    label: 'Resource & Output Mapping',
  },
  { id: SECTIONS.PRODUCT_CLEAN_CHANGEOVER, label: 'Product Clean Changeover' },
  {
    id: SECTIONS.BASE_MATERIAL_CLEAN_CHANGEOVER,
    label: 'Base Material Changeover',
  },
  { id: SECTIONS.SYNCHRONIZED_LINES, label: 'Synchronized Lines' },
  { id: SECTIONS.STERILISER_TYPES, label: 'Steriliser Types' },
  { id: SECTIONS.STERILISER_ROUTING, label: 'Steriliser Routing' },
  // Plant properties, held once per tenant rather than re-entered on every
  // schedule. Both are also seeded into a new schedule by
  // `scheduler_model.py` — a tab without that seeding would let a tenant
  // author master data no schedule ever receives.
  { id: SECTIONS.RESOURCE_SWITCHING, label: 'Resource Switching' },
  { id: SECTIONS.FORMAT_CHANGEOVER, label: 'Format Changeover' },
  {
    id: SECTIONS.RESOURCE_CAPACITY_PROFILE,
    label: 'Resource Capacity Profile',
  },
  // The vessels and what they may hold — two tables behind one entry, the same
  // pairing the per-schedule Raw Data sidebar uses. The per-week half of the
  // feature ("Tank Week State" there) has no master equivalent and is
  // deliberately absent; see SORTanks.view.
  { id: SECTIONS.TANKS, label: 'Holding Tanks' },
  { id: SECTIONS.KITCHEN_CYCLES, label: 'Kitchen Cycles' },
]

export const SchedulerSORPage = () => {
  const [selectedSection, setSelectedSection] = useState(
    SECTIONS.PRODUCTION_REQUIREMENT
  )

  const renderContent = () => {
    switch (selectedSection) {
      case SECTIONS.PRODUCTION_REQUIREMENT:
        return <SORView API="production-requirements" />
      case SECTIONS.WORKSTATION_DOWNTIME:
        return <SORView API="workstation-downtime" />
      case SECTIONS.PRODUCT_MASTER:
        return <SORView API="product-master-data" />
      case SECTIONS.PACKAGING_ROUTES:
        return <SORView<SchedulerPackagingRoutesRowType> API="packing-routes" />
      case SECTIONS.CLEANING_MAPPING:
        return <SORView API="cleaning-mapping" />
      case SECTIONS.RESOURCE_AND_MATERIAL_CONSTRAINTS:
        return <SORView API="resource-and-material-constraints" />
      case SECTIONS.RESOURCE_AND_INPUTS:
        return <SORView API="resource-and-input-mapping" />
      case SECTIONS.RESOURCE_AND_OUTPUT_MAPPING:
        return <SORView API="resource-and-output-mapping" />
      case SECTIONS.PRODUCT_CLEAN_CHANGEOVER:
        return <SORView API="product-clean-changeover" />
      case SECTIONS.BASE_MATERIAL_CLEAN_CHANGEOVER:
        return <SORView API="base-material-clean-changeover" />
      case SECTIONS.SYNCHRONIZED_LINES:
        return <SORSynchronizedLines />
      case SECTIONS.STERILISER_TYPES:
        return <SORView API="steriliser-types" />
      case SECTIONS.STERILISER_ROUTING:
        return <SORSterilizerRouting />
      case SECTIONS.RESOURCE_SWITCHING:
        return <SORView API="resource-switching" />
      case SECTIONS.FORMAT_CHANGEOVER:
        return <SORView API="format-changeover" />
      case SECTIONS.RESOURCE_CAPACITY_PROFILE:
        return <SORView API="resource-capacity-profile" />
      case SECTIONS.TANKS:
        return <SORTanks />
      case SECTIONS.KITCHEN_CYCLES:
        return <SORKitchenCycles />
      default:
        return null
    }
  }

  const selectedLabel = RAW_DATA_ITEMS.find(
    (item) => item.id === selectedSection
  )?.label

  return (
    <SchedulerPageFrame
      crumbs={[{ label: 'System of records' }]}
      // No body padding: the sidebar runs full height against the crumb bar
      // and the left edge, as it does on the schedule page.
      bodySx={{ overflow: 'hidden' }}
    >
      {/* The frame leaves its body unthemed; SOR opts in here so its grids,
          toolbars and text match the schedule page's input tables. */}
      <SchedulerThemeScope sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <SORSidebar
          items={RAW_DATA_ITEMS}
          selected={selectedSection}
          onSelect={setSelectedSection}
        />

        {/* Content area */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            minWidth: 0,
            px: 2.25,
            py: 2,
            background: TOKENS.surface50,
            ...SOR_TABLE_PANEL_SX,
          }}
        >
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.25 }}
          >
            <TableRowsIcon sx={{ fontSize: 18, color: TOKENS.brandTeal }} />
            <SchDisplay>{selectedLabel}</SchDisplay>
          </Box>
          {/* Says what "master" means here: SOR rows reach schedules created
              after the edit, never existing ones (SOR_INPUT_TABLE_ALIGNMENT). */}
          <Box sx={{ fontSize: 12, color: TOKENS.ink400, mb: 2 }}>
            Tenant-wide master data. New schedules copy it when they are
            created; existing schedules keep their own copy.
          </Box>
          {renderContent()}
        </Box>
      </SchedulerThemeScope>
    </SchedulerPageFrame>
  )
}
